import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const ResetDataPanel = () => {
  const { can } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [confirmShop, setConfirmShop] = useState("");
  const [password, setPassword] = useState("");
  const [deleteDrive, setDeleteDrive] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!can("users:manage")) return null;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", {
        confirm,
        confirm_shop: confirmShop,
        password,
        delete_drive: deleteDrive,
      });
      const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`System reset — ${total} test records removed`);
      if (data.drive) toast.success(data.drive);
      setConfirm("");
      setConfirmShop("");
      setPassword("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#C1272D]/40" data-testid="reset-data-panel">
      <div className="border-b border-[#C1272D]/40 bg-[#C1272D]/5 px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#C1272D]">
          <AlertTriangle className="h-4 w-4" /> Reset system data
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Permanently deletes every invoice, contract, loan, repair, buying contract and
          customer, plus KYC records, ID requests, the queue, account credit and ledger
          entries, Kuber payments received, customer portal logins, uploaded files and
          shared document links — and restarts invoice numbering from the beginning.
          Staff logins, store details, trading hours, gold rates, the item catalogue and
          the audit trail are kept. This cannot be undone.
        </p>
      </div>
      <div className="grid gap-4 p-5 sm:grid-cols-3 sm:items-end">
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Type RESET to confirm
          </Label>
          <Input
            className="h-10 rounded-none"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="RESET"
            data-testid="reset-confirm-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Then type DHUNNA JEWELLERS
          </Label>
          <Input
            className="h-10 rounded-none"
            value={confirmShop}
            onChange={(e) => setConfirmShop(e.target.value)}
            placeholder="DHUNNA JEWELLERS"
            data-testid="reset-confirm-shop-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Your admin password
          </Label>
          <Input
            type="password"
            className="h-10 rounded-none"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="reset-password-input"
          />
        </div>
        <label className="flex items-start gap-2 text-xs text-muted-foreground sm:col-span-2">
          <input
            type="checkbox"
            checked={deleteDrive}
            onChange={(e) => setDeleteDrive(e.target.checked)}
            className="mt-0.5"
            data-testid="reset-delete-drive-checkbox"
          />
          <span>
            Also delete the old backup folder in Google Drive. Leave this unticked to
            keep it, renamed to “TESTING DATA” with today’s date. Ticked, it goes to the
            Drive bin and is recoverable for 30 days.
          </span>
        </label>
        <Button
          onClick={run}
          disabled={
            busy ||
            confirm !== "RESET" ||
            confirmShop.trim().toUpperCase() !== "DHUNNA JEWELLERS" ||
            !password
          }
          className="h-10 rounded-none bg-[#C1272D] font-semibold text-white hover:bg-[#A11F24]"
          data-testid="reset-data-button"
        >
          {busy ? "Wiping…" : "Wipe all records"}
        </Button>
      </div>
    </div>
  );
};
